import React from "react";
import { IResourceComponentsProps, useShow, useTranslate, useNavigation } from "@refinedev/core";
import { DateField, Show } from "@refinedev/mui";
import {
    Stack,
    Typography,
    Box,
    Grid,
    Card,
    CardContent,
    Avatar,
    useTheme,
    alpha,
    Divider,
    Chip,
    Button,
    Paper,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    IconButton,
    Tooltip
} from "@mui/material";
import {
    CalendarMonth,
    Update,
    People,
    Shield,
    Edit,
    CheckCircle,
    Warning,
    Groups,
    AdminPanelSettings,
    PersonOutline
} from "@mui/icons-material";
import { Building2, Users, AlertCircle } from "lucide-react";
import { LoadingSkeleton } from "../../components/common/LoadingSkeleton";
import { SectionCard } from "../../components/common/SectionCard";
import { StatusBadge } from "../../components/common/StatusBadge";
import { useAuth } from "../../contexts/auth-context";
import { canModifyResource, UserRole } from "../../utils/role-permissions";

export const CompanyShow: React.FC<IResourceComponentsProps> = () => {
    const translate = useTranslate();
    const theme = useTheme();
    const { queryResult } = useShow();
    const { data, isLoading } = queryResult;
    const { list, edit } = useNavigation();
    const { user: currentUser } = useAuth();

    const record = data?.data;

    // Check if user can modify this company
    const canModify = currentUser?.role
        ? canModifyResource(currentUser.role as UserRole, 'company') &&
          (currentUser.role === 'ADMIN' ||
           (currentUser.role === 'MODERATOR' && currentUser.entrepriseId === record?.id))
        : false;

    const isGuest = currentUser?.role === 'GUEST';

    if (isLoading) {
        return <LoadingSkeleton />;
    }

    if (!record) {
        return (
            <Show
                headerButtons={({ defaultButtons }) => (
                    <>
                        {defaultButtons}
                    </>
                )}
            >
                <Box sx={{ p: 3, textAlign: 'center' }}>
                    <Typography color="text.secondary">
                        {translate("company.notFound", "Organisation non trouvée")}
                    </Typography>
                </Box>
            </Show>
        );
    }

    const userPercentage = record.limitUser ? (record.count / record.limitUser) * 100 : 0;
    const isNearLimit = userPercentage >= 80;
    const isActive = record.count > 0;


    return (
        <Show
            headerButtons={false}
            headerProps={{
                sx: { display: 'none' }
            }}
            breadcrumb={false}
            goBack={false}
            title={false}
        >
            <Grid container spacing={3}>
                {/* Header Card */}
                <Grid item xs={12}>
                    <Card
                        sx={{
                            background: theme.palette.mode === 'dark'
                                ? `linear-gradient(135deg, ${alpha(theme.palette.primary.dark, 0.1)} 0%, ${alpha(theme.palette.background.paper, 0.9)} 100%)`
                                : `linear-gradient(135deg, ${alpha(theme.palette.primary.light, 0.05)} 0%, ${theme.palette.background.paper} 100%)`,
                            border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                            overflow: 'visible'
                        }}
                    >
                        <CardContent sx={{ p: 4 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                                <Avatar
                                    sx={{
                                        width: 80,
                                        height: 80,
                                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                                        color: theme.palette.primary.main,
                                        boxShadow: theme.shadows[3]
                                    }}
                                >
                                    <Building2 size={40} />
                                </Avatar>

                                <Box sx={{ flex: 1 }}>
                                    <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }}>
                                        <Typography variant="h4" sx={{ fontWeight: 600 }}>
                                            {record.nom}
                                        </Typography>
                                        <StatusBadge
                                            status={isActive ? "active" : "inactive"}
                                            label={isActive ? translate("company.active", "Active") : translate("company.inactive", "Inactive")}
                                            icon={isActive ? <CheckCircle /> : <Warning />}
                                        />
                                    </Stack>

                                    <Stack direction="row" spacing={3} alignItems="center">
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <CalendarMonth sx={{ fontSize: 18, color: 'text.secondary' }} />
                                            <Typography variant="body2" color="text.secondary">
                                                {translate("company.fields.createdAt", "Créé le")}:
                                            </Typography>
                                            <DateField value={record.createdAt} format="LL" />
                                        </Box>

                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <Update sx={{ fontSize: 18, color: 'text.secondary' }} />
                                            <Typography variant="body2" color="text.secondary">
                                                {translate("company.fields.updatedAt", "Modifié le")}:
                                            </Typography>
                                            <DateField value={record.updatedAt} format="LL" />
                                        </Box>
                                    </Stack>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                {/* User Limit Card */}
                <Grid item xs={12}>
                    <SectionCard
                        title={translate("company.sections.userLimit.title")}
                        icon={<People />}
                        subtitle={translate("company.sections.userLimit.desc")}
                    >
                        <Box sx={{ p: 2 }}>
                            <Stack spacing={3}>
                                <Box>
                                    <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 1 }}>
                                        <Typography variant="h3" sx={{ fontWeight: 600 }}>
                                            {record.count || 0}
                                        </Typography>
                                        <Typography variant="h5" color="text.secondary">
                                            / {record.limitUser || '∞'}
                                        </Typography>
                                    </Stack>

                                    {record.limitUser && (
                                        <>
                                            <Box
                                                sx={{
                                                    width: '100%',
                                                    height: 8,
                                                    bgcolor: alpha(theme.palette.divider, 0.2),
                                                    borderRadius: 4,
                                                    overflow: 'hidden',
                                                    mb: 1
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        width: `${Math.min(userPercentage, 100)}%`,
                                                        height: '100%',
                                                        bgcolor: isNearLimit
                                                            ? theme.palette.error.main
                                                            : theme.palette.success.main,
                                                        transition: 'width 0.3s ease',
                                                        boxShadow: `0 0 4px ${isNearLimit
                                                            ? theme.palette.error.main
                                                            : theme.palette.success.main}`
                                                    }}
                                                />
                                            </Box>

                                            <Stack direction="row" justifyContent="space-between">
                                                <Typography variant="caption" color="text.secondary">
                                                    {translate("company.userLimit.usage", "Utilisation")}: {userPercentage.toFixed(0)}%
                                                </Typography>
                                                {isNearLimit && (
                                                    <Chip
                                                        label={translate("company.userLimit.nearLimit", "Proche de la limite")}
                                                        size="small"
                                                        color="error"
                                                        icon={<AlertCircle size={14} />}
                                                    />
                                                )}
                                            </Stack>
                                        </>
                                    )}
                                </Box>

                                <Divider />

                                <Box>
                                    <Typography variant="body2" color="text.secondary" gutterBottom>
                                        {translate("company.userLimit.info", "Informations sur la limite")}
                                    </Typography>
                                    <List dense>
                                        <ListItem disablePadding>
                                            <ListItemIcon sx={{ minWidth: 36 }}>
                                                <Users size={18} />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={translate("company.userLimit.current", "Utilisateurs actuels")}
                                                secondary={record.count || 0}
                                            />
                                        </ListItem>
                                        <ListItem disablePadding>
                                            <ListItemIcon sx={{ minWidth: 36 }}>
                                                <Shield sx={{ fontSize: 18 }} />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={translate("company.userLimit.max", "Limite maximale")}
                                                secondary={record.limitUser || translate("company.userLimit.unlimited", "Illimité")}
                                            />
                                        </ListItem>
                                        <ListItem disablePadding>
                                            <ListItemIcon sx={{ minWidth: 36 }}>
                                                <Groups sx={{ fontSize: 18 }} />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={translate("company.userLimit.available", "Places disponibles")}
                                                secondary={record.limitUser ? Math.max(0, record.limitUser - record.count) : translate("company.userLimit.unlimited", "Illimité")}
                                            />
                                        </ListItem>
                                    </List>
                                </Box>
                            </Stack>
                        </Box>
                    </SectionCard>
                </Grid>

                {/* Actions Card */}
                {canModify && !isGuest && (
                    <Grid item xs={12}>
                        <Paper
                            sx={{
                                p: 3,
                                background: alpha(theme.palette.info.main, 0.05),
                                border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`
                            }}
                        >
                            <Stack direction="row" spacing={2} alignItems="center">
                                <AdminPanelSettings sx={{ color: theme.palette.info.main }} />
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                                        {translate("company.admin.title", "Actions d'administration")}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {translate("company.admin.desc", "Gérez les paramètres de votre organisation")}
                                    </Typography>
                                </Box>
                                <Button
                                    variant="contained"
                                    startIcon={<Edit />}
                                    onClick={() => record.id && edit('company', record.id)}
                                    color="info"
                                    disabled={!record.id}
                                >
                                    {translate("buttons.edit", "Modifier")}
                                </Button>
                                <Tooltip title={translate("company.viewUsers", "Voir les utilisateurs")}>
                                    <IconButton
                                        onClick={() => list('users')}
                                        sx={{
                                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                                            '&:hover': {
                                                bgcolor: alpha(theme.palette.primary.main, 0.2),
                                            }
                                        }}
                                    >
                                        <PersonOutline />
                                    </IconButton>
                                </Tooltip>
                            </Stack>
                        </Paper>
                    </Grid>
                )}
            </Grid>
        </Show >
    );
};
